import { useState, type FormEvent } from "react";
import { ArrowUp, Loader2, Search, Sparkles } from "lucide-react";
import { useNavigate } from "react-router";
import { useProjectContext } from "@/app/project-context";
import { HazardStripe } from "@/components/gb/hazard-stripe";
import { Input } from "@/components/ui/input";
import { useCreateThread } from "@/features/chat/chat-api";

/**
 * The knowledge-base card — the loudest object on the screen, and the only
 * place the dark chrome, the brand outline and the hazard stripe all meet.
 *
 * Submitting **starts a conversation**: it creates a thread carrying the
 * question and navigates into it, where the answer streams in with its sources.
 * It used to hand the text to `/brain?q=` as a substring filter, which made the
 * placeholder — "Ask me anything" — a promise the app did not keep.
 *
 * The Brain screen keeps its own "Search this site's Brain" input, and the two
 * are deliberately different things: this one asks, that one filters.
 */
export function SiteBrainHero({ projectName }: { projectName: string }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { projectId } = useProjectContext();
  const createThread = useCreateThread(projectId);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const query = q.trim();
    if (!query || createThread.isPending) return;
    // The question titles the record, and rides along in navigation state so
    // the ask screen asks it on arrival. Not in the URL: a question is prose,
    // and a query string full of it is both ugly and a second place the same
    // text lives. Not stored as a message either — `ask` records the user turn
    // itself, and writing it here too would show it twice.
    const thread = await createThread.mutateAsync(query);
    setQ("");
    void navigate(`/projects/${projectId}/brain/ask/${thread.id}`, {
      state: { ask: query },
    });
  };

  return (
    <div className="mb-[13px] overflow-hidden rounded-panel border-[1.5px] border-brand bg-chrome shadow-hero">
      <HazardStripe height={7} />
      <div className="px-[22px] pt-[15px] pb-[17px]">
        <div className="mb-[11px] flex flex-wrap items-center gap-[9px]">
          <span className="flex text-brand-bright">
            <Sparkles size={20} />
          </span>
          <span className="font-display text-[0.72rem] font-semibold tracking-[0.12em] text-brand-bright uppercase">
            Site Brain · Knowledge base
          </span>
          <span className="text-[0.8rem] text-white/50">
            Every email, message, call &amp; document — one answer
          </span>
        </div>

        <form className="relative" onSubmit={(e) => void submit(e)}>
          <span className="pointer-events-none absolute top-1/2 left-[18px] flex -translate-y-1/2 text-brand">
            <Search size={18} />
          </span>
          <Input
            variant="hero"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Ask me anything about ${projectName}`}
            aria-label={`Ask Site Brain about ${projectName}`}
          />
          <button
            type="submit"
            disabled={createThread.isPending}
            aria-label="Ask Site Brain"
            className="absolute top-1/2 right-[9px] flex size-[46px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-input border-none bg-brand text-white transition-colors hover:bg-brand-hover disabled:opacity-70"
          >
            {createThread.isPending ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <ArrowUp size={18} />
            )}
          </button>
        </form>
        {createThread.error && (
          <p role="alert" className="mt-2 text-[0.8rem] font-semibold text-danger">
            Couldn’t start that conversation.
          </p>
        )}
      </div>
    </div>
  );
}
